import RegisterForm from '../../components/registerForm';
import Button from '@/shared/ui/button';

import './register.css';

import nabpLogo from '@/assets/images/register/nabp.png';
import ACHCLogo from '@/assets/images/register/ACHC.svg';
import uracLogo from '@/assets/images/register/urac.svg';

import { ICustomersRegisterRequest } from '@/shared/endpoints/customers/models/request';
import { useAppSelector } from '@/shared/hooks/store';
import { useTheme } from '@mui/material';
import { selectLoginLoading } from '@/store/slices/auth/selectors';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/shared/constants/routes';
import HipaaCompliant from '@/assets/images/hipaa-compliant.svg';

const logos = [
    {
        id: 'nabp',
        img: nabpLogo,
        link: 'https://app.us.openbadges.me/view/#!/badge?badgeid=18c10173-c11c-4ee9-8b56-fee4dbf5c1af'
    },
    {
        id: 'medbox',
        img: 'https://static.legitscript.com/seals/400488.png',
        link: 'https://www.legitscript.com/websites/?checker_keywords=medbox.com'
    },
    {
        id: 'achc',
        img: ACHCLogo,
        link: 'https://www.achc.org/'
    },
    {
        id: 'urac',
        img: uracLogo,
        link: 'https://www.urac.org/'
    }
];

interface IProps {
    onSubmit(form: ICustomersRegisterRequest): Promise<void>;
};

const Register = ({ onSubmit }: IProps) => {
    const theme = useTheme();
    const isLoading = useAppSelector(selectLoginLoading);

    return (
        <>
            <div className="flex w-full flex-col items-start lg:flex-row">
                <div className="relative lg:w-1/2">
                    <div className="banner relative z-10 w-screen lg:w-full">
                        {/* <img
                                className="w-screen relative z-10 lg:w-full h-[240px] sm:h-[300px] md:h-[360px] lg:h-auto object-cover"
                                src={Banner}
                                alt="Medbox icon"
                            /> */}
                    </div>
                    <div className="signup-gradient-overlay absolute inset-0 top-[800px] hidden lg:block xl:top-[900px]"></div>
                    {/* <div className="block lg:hidden absolute inset-0 signup-gradient-overlay-v2"></div> */}

                    <div className="relative -top-8 bottom-1/2 left-1/2 z-20 hidden w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded xl:top-32 xl:flex">
                        <div className="relative max-w-[94%] overflow-hidden rounded">
                            <iframe
                                className="register__video max-w-[100%]"
                                width="560"
                                height="315"
                                src="https://www.youtube.com/embed/5HHOUs0tgWI"
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>

                <div className="content relative z-10 -mt-2 w-full p-5 pb-0 sm:px-24 lg:mt-20 lg:w-1/2 lg:pl-[70px] ms:mt-3 xs:mt-8 xs:p-16 xs:pt-0">
                    <div className="2xl:w-[560px] 3xl:w-[715px]">
                        <h3 data-testid="register-top-text" className="mb-4 font-atkinsonHyperlegible text-base uppercase lg:mb-3">
                            get ready to Switch to a Better, Simpler Pharmacy
                        </h3>
                        <h2 data-testid="register-title" className="register__heading mb-6 font-agrandir text-4xl lg:mb-8 lg:text-[36px] lg:leading-[42px] xl:text-50 xl:leading-[65px] 3xl:max-w-[70%]">
                            Create your account now <span className="text-primary-400">with MedBox</span>
                        </h2>

                        <RegisterForm
                            onSubmit={onSubmit}
                            isLoading={isLoading}
                        />
                        <Link data-testid="button-login-existing-account-link" to={ROUTES.LOGIN} className={`${isLoading ? 'pointer-events-none' : ''} register__link_to_login`}>
                            <Button
                                className="register__login_button"
                                variant="outlined"
                                sx={{
                                    mt: '20px',
                                    width: '100%',
                                    fontWeight: '600',
                                    [theme.breakpoints.down('lg')]: {
                                        mt: '10px',
                                        fontSize: 16,
                                        paddingTop: '6.5px',
                                        paddingBottom: '6.5px'
                                    }
                                }}
                                disabled={isLoading}
                                testId="button-login-existing-account"
                            >
                                Log in to Existing Account
                            </Button>
                        </Link>

                        <div className="mt-8 flex w-full items-center justify-center lg:mt-12">
                            <img src={HipaaCompliant} alt="Hipaa compliant" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bottom-gradient-overlay relative z-0 -mb-8 flex w-full flex-col items-center gap-9 px-4 pb-28 pt-10 sm:px-24 lg:gap-[38px] lg:pb-12 lg:pt-24">
                <p data-testid="register-bottom-medbox-text" className="text-center font-atkinsonHyperlegible text-lg lg:max-w-[65%] xl:text-22">
                    <strong className="italic">MedBox by AmeriPharma’s accreditation</strong> badges demonstrate our commitment to
                    delivering superior pharmacy services that meet nationally-recognized standards of care.
                </p>
                <div className="grid w-[767px] max-w-full grid-cols-2 items-center justify-between gap-6 md:grid-cols-4">
                    {logos.map((logo, index) => (
                        <Link data-testid={`regsiter-bottom-link-${index}`} to={logo.link} target="_blank" className={`flex w-full items-center justify-center register__badge_${logo.id}`} key={logo.id}>
                            <img className="h-[102px] xs:w-[102px]" src={logo.img} alt={`${logo.id.toUpperCase()} logo`} />
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};

export default Register;
